import React, { useState } from 'react';
import { Box, Button, Stack, Typography, Paper, IconButton, CircularProgress, Divider, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { HttpService } from 'core/services/http';
import { CVHttpService } from 'features/feature_cv/services';
import { Utils } from 'core/utils';
import CertificateFormDialog from 'features/feature_cv/components/dialogs/CertificateFormDialog';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

const formatExpirationDate = (month, year) => {
    if (!month || !year) return 'No expiration';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[month - 1]} ${year}`;
};

function CertificatesSection({ cvId }) {
    const queryClient = useQueryClient();
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCertificate, setEditingCertificate] = useState(null);

    const { data, isLoading, refetch } = useQuery(
        ['cv_certificates', cvId],
        async () => {
            const service = new HttpService();
            const response = await service.get(`/job/cv/certificate/?cv=${cvId}`, null);
            return Array.isArray(response) ? response : (response?.results || []);
        },
        { enabled: !!cvId }
    );

    const deleteMutation = useMutation(
        (id) => CVHttpService.deleteCertificate(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['cv_certificates', cvId]);
            }
        }
    );

    const certificates = data || [];

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this certificate?')) return;
        deleteMutation.mutate(id);
    };

    const handleSave = async () => {
        await refetch();
        setOpenDialog(false);
        setEditingCertificate(null);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                    Certificates ({certificates.length})
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingCertificate(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Certificate
                </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {certificates.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No certificates added yet.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {certificates.map((cert) => (
                        <Paper key={cert.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {formatText(cert.title)}
                                    </Typography>
                                    {cert.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {formatText(cert.description)}
                                        </Typography>
                                    )}
                                    {(cert.expiration_month || cert.expiration_year) && (
                                        <Chip
                                            label={`Expires: ${formatExpirationDate(cert.expiration_month, cert.expiration_year)}`}
                                            size="small"
                                            color={cert.expiration_year && cert.expiration_year < new Date().getFullYear() ? 'error' : 'default'}
                                            variant="outlined"
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                </Box>
                                <Stack direction="row" spacing={0.5}>
                                    <IconButton color="primary"
                                        size="small"
                                        onClick={() => {
                                            setEditingCertificate(cert);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(cert.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <CertificateFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingCertificate(null);
                }}
                onSave={handleSave}
                cvId={cvId}
                certificate={editingCertificate}
            />
        </Paper>
    );
}

export default CertificatesSection;

